import { useEffect, useState } from "react";
import SVGManipulation from "../helpers/SVGManipulation";
import BabyBody from "./BabyBody";
import SVGImagePositioning from "../helpers/SVGImagePositioning";
import { Visibility } from "../helpers/Enums";
import useLabourViewStore from "../hooks/useLabourViewStore";

const visualizationLabels = {
  PelvicInlet: "Bekkeninngang",
  IschialSpine: "Spinae",
  PelvicFloor: "Bekkenbunn",
};

// The SVG descent level varies in pixel differences for each level,
// requiring precise numbers to accurately position the descent arrow and baby's head.
const pixelDescentLevels: { [level: number]: number } = {
  "-5": 0,
  "-4": 15.2,
  "-3": 30.5,
  "-2": 46,
  "-1": 61.2,
  0: 76.5,
  1: 92,
  2: 107.2,
  3: 122.4,
  4: 137.8,
};

const mapSvgLocationToDescentRange = (positionY: number) => {
  // Defines the clickable/touchable area within the SVG to trigger descent changes.
  const descentAreaInSvgImage = {
    startX: 0,
    startY: 213,
    endX: 500,
    endY: 351,
  };

  // Represents the valid top descent value that can be set within the SVG.
  const validTopDescentValue = -5;

  // Calculates the mapped value corresponding to the descent value and its position in the SVG.
  const rangeStart = descentAreaInSvgImage.startY;
  const rangeEnd = descentAreaInSvgImage.endY;
  const value = Math.max(Math.min(positionY, rangeEnd), rangeStart);
  // Divides by 9 because there are 9 possible steps in the range from -5 to 4.
  const position = (value - rangeStart) / ((rangeEnd - rangeStart) / 9);
  const mappedValue = validTopDescentValue + position;
  return Math.round(mappedValue);
};

// Retrieves the new descent value when a mouse/touch event is triggered.
// Obtains the pixel coordinates in the SVG image for the event location and maps it to the descent range.
const getNewDescentValueAfterMouseEvents = (
  clientXCoordinate: number,
  clientYCoordinate: number
) => {
  // Retrieves the SVG element to calculate the precise pixel location of a click/touch event.
  const svgElement: SVGSVGElement | null =
    document.querySelector<SVGSVGElement>("#DescentSVG");
  // Checks for null since TypeScript enforces strict type checking.
  if (svgElement != null) {
    const positionClickedinSvg = SVGManipulation.pixelToSvgLocation(
      svgElement,
      clientXCoordinate,
      clientYCoordinate
    );
    return mapSvgLocationToDescentRange(positionClickedinSvg.y);
  }
};

// Retrieves the pixel position of the top line in the descent scale (SVG).
// This value is crucial for the dragging effect to correctly position the arrow
// and the baby's head relative to the current mouse/touch point.
const getTopLineInDescentScalePxPositionY = () => {
  return SVGManipulation.getSvgElementPxLocationById(
    "#TopLineInDescentScaleElement"
  ).y;
};

// Retrieves the pixel position of the bottom line in the descent scale (SVG),
// using the last left line on the scale as a reference point.
const getBottomLineInDescentScalePxPositionY = () => {
  return SVGManipulation.getSvgElementPxLocationById(
    "#BottomLineInDescentScaleElement"
  ).y;
};

// React component for visualizing and interacting with SVG elements depicting childbirth
// Manages baby's descent level, body position, rotation, and interaction states
// Utilizes helper functions for SVG manipulation and event handling (mouse/touch)
// Dynamically adjusts SVG visualization based on state changes and user input
function CervixDescent() {
  // Variables

  // State manager for the current level of descent for the baby.
  const babyDescentLevel = useLabourViewStore(
    (state) => state.babyDescentLevel
  );
  const setBabyDescentLevel = useLabourViewStore(
    (state) => state.setBabyDescentLevel
  );

  // State manager for the current position and rotation of the baby's body.
  const babyBodyPosition = useLabourViewStore(
    (state) => state.babyBodyPosition
  );
  const setBabyBodyPosition = useLabourViewStore(
    (state) => state.setBabyBodyPosition
  );
  const babyBodyRotation = useLabourViewStore(
    (state) => state.babyBodyRotation
  );
  const setBabyBodyRotation = useLabourViewStore(
    (state) => state.setBabyBodyRotation
  );

  // Indicates whether the progress player mode is currently active.
  const isPlayerInProgress = useLabourViewStore(
    (state) => state.isPlayerModeActive
  );

  // Add synclitism from store
  const synclitism = useLabourViewStore((state) => state.babyHeadPosition);

  // Initial coordinates for the baby's position
  const [babyPositionX, setBabyPositionX] = useState(() => {
    const babyPositionCoordinates =
      SVGImagePositioning.getBabyBodyImagesInitialPosition(
        babyBodyPosition,
        babyBodyRotation,
        synclitism
      );
    return babyPositionCoordinates?.posX ?? 68;
  });

  const [babyPositionY, setBabyPositionY] = useState(() => {
    const babyPositionCoordinates =
      SVGImagePositioning.getBabyBodyImagesInitialPosition(
        babyBodyPosition,
        babyBodyRotation,
        synclitism
      );
    return babyPositionCoordinates?.posY ?? 262;
  });

  // Controls whether the arrow element is draggable (true) or not (false).
  const [arrowDraggable, setArrowDraggable] = useState(false);

  // Calculates the precise pixel value for moving the arrow and the baby's body
  // using the transform-translate function in SVG elements.
  const [pxDescentLevel, setPxDescentLevel] = useState(
    pixelDescentLevels[babyDescentLevel]
  );

  // Initial pixel position from the top for both the arrow and baby's body relative to the SVG.
  const initialPxPositionY = 219;

  // Represents the SVG element used for visualizing rotation effects.
  const descentSVGElement =
    document.querySelector<SVGSVGElement>("#DescentSVG");

  // Specifies the clickable/touchable area within the SVG using coordinates.
  const clickableTouchableAreaCoordinates =
    SVGManipulation.getSvgElementCoordinates(descentSVGElement!);

  // Positions the descent level (number from -5 to 4) at the correct location within the SVG.
  const descentNumberLevel = pxDescentLevel + initialPxPositionY;

  // Handles click/touch events within the SVG for interactive functionality.
  const handleUserClickOrTouch = (event: React.MouseEvent) => {
    if (isPlayerInProgress) return;

    event.preventDefault();
    event.stopPropagation();
    const newDescentValue = getNewDescentValueAfterMouseEvents(
      event.clientX,
      event.clientY
    );
    setBabyDescentLevel(newDescentValue!);
    const pxDescentLvl = pixelDescentLevels[newDescentValue!];
    setPxDescentLevel(pxDescentLvl);
  };

  // Click / Touch shared actions

  // Begins the click/touch interaction.
  const startClickOrTouch = () => {
    setArrowDraggable(true);
  };

  // Handles mouse movement or touch dragging.
  const moveWhileClickOrTouch = (clientYCoordinate: number) => {
    // Updates the pixel descent level as the user moves the arrow, producing a dragging effect.
    // Refreshes continuously while the mouse is moved, constrained within the scale's top and bottom limits.
    if (
      clientYCoordinate > getTopLineInDescentScalePxPositionY() &&
      clientYCoordinate < getBottomLineInDescentScalePxPositionY()
    ) {
      setPxDescentLevel(
        Math.abs(clientYCoordinate - getTopLineInDescentScalePxPositionY())
      );
    }
  };

  // Handles the end of a mouse click or touch dragging interaction.
  const endClickOrTouch = (
    event: React.MouseEvent | React.TouchEvent,
    clientXCoordinate: number,
    clientYCoordinate: number
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setArrowDraggable(false);

    // Check if click/touch event is outside clickable/touchable area to cancel dragging and reset arrow
    if (
      clientXCoordinate < clickableTouchableAreaCoordinates!.left ||
      clientXCoordinate > clickableTouchableAreaCoordinates!.right ||
      clientYCoordinate < clickableTouchableAreaCoordinates!.top ||
      clientYCoordinate > clickableTouchableAreaCoordinates!.bottom
    ) {
      cancelClickOrTouch(event);
      return;
    }

    // Retrieves the new descent value based on the mouse-up position and adjusts
    // the position of the arrow/baby's head accordingly after dragging or dropping.
    const newDescentValue = getNewDescentValueAfterMouseEvents(
      clientXCoordinate,
      clientYCoordinate
    );
    setBabyDescentLevel(newDescentValue!);
    const pxDescentLvl = pixelDescentLevels[newDescentValue!];
    setPxDescentLevel(pxDescentLvl);
  };

  // Handles scenarios where the mouse moves out of the clicking area or the touch event is cancelled
  // (interrupted or moved outside of the dragging area).
  const cancelClickOrTouch = (event: React.MouseEvent | React.TouchEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setArrowDraggable(false);
    // Cancels dragging functionality and resets the arrow level to its previous value
    // if the mouse is moved or clicked outside the arrow element.
    setBabyDescentLevel(babyDescentLevel);
    const pxDescentLvl = pixelDescentLevels[babyDescentLevel];
    setPxDescentLevel(pxDescentLvl);
  };

  useEffect(() => {
    setBabyBodyPosition(babyBodyPosition);
    // Calculate the initial coordinates for positioning the baby.
    const babyPositionCoordinates =
      SVGImagePositioning.getBabyBodyImagesInitialPosition(
        babyBodyPosition,
        babyBodyRotation,
        synclitism
      );

    // Updates the baby's position coordinates, setting the X and Y positions accordingly.
    if (babyPositionCoordinates !== null) {
      setBabyPositionX(babyPositionCoordinates.posX);
      setBabyPositionY(babyPositionCoordinates.posY);
    }
    setBabyBodyRotation(babyBodyRotation);

    // Adjusts the visualization when the baby's descent level changes after registering data in the form.
    const pxDescentLvl = pixelDescentLevels[babyDescentLevel];
    setPxDescentLevel(pxDescentLvl);
  }, [
    babyBodyPosition,
    setBabyBodyPosition,
    babyBodyRotation,
    setBabyBodyRotation,
    babyDescentLevel,
    synclitism,
  ]);

  // Most of the SVG images corresponding the baby descent, have a width of 280px.
  // This is used to center the baby body image.
  const SVG_WIDTH = 280;
  const SVG_CENTER_X = SVG_WIDTH / 2;

  return (
    <div className="svg-clickable-area" onClick={handleUserClickOrTouch}>
      <svg
        width="470"
        height="400"
        viewBox="0 0 470 400"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        id="DescentSVG"
      >
        <g clipPath="url(#clip0_255_14250)">
          {/* Start - Pelvis top part */}
          <path
            d="M409.985 124.149C406.889 119.802 403.015 112.471 398.616 111.116C394.236 109.771 387.266 105.959 362.991 108.143C338.734 110.298 312.395 101.612 283.992 134.463L284.208 135.075C249.577 147.788 213.014 146.404 213.014 146.404C213.014 146.404 176.423 147.788 141.801 135.056L142.017 134.453C113.614 101.603 87.2937 110.298 63.0183 108.133C38.7616 105.95 31.7923 109.752 27.393 111.107C23.0126 112.462 19.1386 119.792 16.0432 124.14C12.9291 128.469 9.86181 136.354 24.2976 144.494C38.7335 152.643 32.8241 163.776 43.1421 169.742C53.4601 175.717 55.1109 182.173 55.7957 188.741C57.1839 202.066 64.7911 216.275 70.1096 225.93H119.598C119.598 205.143 193.757 211.194 213.023 210.507C232.271 211.194 306.439 205.143 306.439 225.93H355.909C361.227 216.275 368.825 202.075 370.223 188.75C370.898 182.182 372.54 175.727 382.858 169.751C393.194 163.785 387.276 152.653 401.702 144.504C416.138 136.364 413.071 128.487 409.975 124.149H409.985Z"
            fill="white"
          />
          <path
            d="M70.3727 225.93C70.9637 227.022 71.5546 228.104 72.1362 229.167C73.3837 231.755 75.2128 234.183 75.5974 237.1C75.5974 237.081 75.241 237.062 75.241 237.062C74.8095 234.296 72.9991 231.849 71.761 229.365L69.8662 225.93"
            fill="#2E7FA1"
          />
          <path
            d="M69.8564 225.93L67.5865 221.827C64.8569 216.774 62.2774 211.627 60.1294 206.291C58.0095 200.946 56.2836 195.394 55.5519 189.644C55.2424 186.784 54.8484 183.97 53.7791 181.344C51.6968 175.915 46.8942 172.405 42.0823 169.506C39.6153 167.916 37.7112 165.46 36.6137 162.769C35.4694 160.087 34.7283 157.311 33.5277 154.742C32.0738 151.542 29.7382 148.757 26.896 146.706C20.649 142.716 11.269 137.813 13.2763 128.854C13.886 125.966 15.9027 123.632 17.4504 121.204C19.983 117.534 22.3092 113.262 26.3708 111.022C27.2994 110.655 28.9972 110.081 29.9633 109.761C40.9754 106.063 52.7942 106.929 64.1534 107.804C75.5407 108.679 87.0688 107.503 98.4373 109.102C102.565 109.554 107.058 110.73 111.035 111.954C112.292 112.509 115.828 113.713 117.019 114.4C117.957 114.899 119.927 115.85 120.865 116.348C122.281 117.111 124.973 118.936 126.352 119.764C128.238 121.27 131.361 123.444 133.078 125.156C134.879 126.765 137.655 129.344 139.222 131.179L142.148 134.341L141.867 134.585C140.113 132.75 137.749 130.097 135.948 128.375L132.768 125.504C131.042 123.848 127.975 121.693 126.043 120.216C124.701 119.36 121.897 117.619 120.574 116.875C119.599 116.414 117.713 115.445 116.747 114.974C115.556 114.353 112.123 113.111 110.81 112.584C106.842 111.389 102.433 110.232 98.3341 109.799C86.9843 108.237 75.5032 109.451 64.0877 108.604C52.8224 107.776 41.0974 106.929 30.2447 110.617C29.4286 110.947 27.562 111.483 26.7553 111.85C22.9002 114.024 20.6865 118.146 18.2289 121.731C16.7656 124.065 14.7489 126.323 14.1674 129.08C12.3289 137.615 21.4744 142.123 27.4026 146.028C30.3573 148.145 32.7867 151.091 34.2781 154.403C35.4975 157.047 36.2385 159.852 37.3454 162.458C38.4053 165.074 40.1687 167.342 42.5137 168.876C47.4007 171.812 52.344 175.529 54.4545 181.09C55.5519 183.819 55.9459 186.746 56.2461 189.578C56.9496 195.243 58.638 200.748 60.7297 206.065C63.2717 212.699 66.8924 219.522 70.3723 225.939"
            fill="#2E7FA1"
          />
          <path
            d="M304.507 220.002C292.182 207.204 243.509 210.225 225.415 210.272C221.278 210.347 217.142 210.46 213.015 210.347C208.897 210.46 204.723 210.347 200.595 210.272C185.775 210.432 119.299 205.783 119.111 225.949C119.111 225.949 120.049 225.949 120.04 225.949C119.984 221.752 123.529 218.788 127.047 217.094C138.359 212.022 151.144 211.57 163.366 210.817C175.335 210.281 188.533 210.516 200.586 210.695C204.723 210.742 208.859 210.836 213.005 210.695C217.151 210.836 221.288 210.742 225.424 210.695C239.776 210.912 305.614 206.903 305.97 225.949L306.908 225.94C306.899 223.747 305.999 221.564 304.507 220.002Z"
            fill="#2E7FA1"
          />
          <path
            d="M356.097 225.93C354.427 228.97 352.795 232.009 351.313 235.049C351.041 235.698 350.778 236.385 350.769 237.025L350.422 237.081C350.806 234.183 352.636 231.736 353.883 229.148L355.628 225.93"
            fill="#2E7FA1"
          />
          <path
            d="M355.628 225.93L357.983 221.583C360.665 216.52 363.198 211.373 365.29 206.037C367.381 200.72 369.07 195.215 369.773 189.551C370.074 186.718 370.468 183.782 371.565 181.063C373.666 175.501 378.619 171.784 383.506 168.848C385.851 167.314 387.605 165.046 388.665 162.43C389.772 159.833 390.513 157.029 391.732 154.375C393.214 151.063 395.662 148.117 398.608 146C404.536 142.104 413.691 137.578 411.843 129.043C411.261 126.305 409.254 124.037 407.781 121.694C405.324 118.108 403.101 113.977 399.255 111.813C398.42 111.493 396.666 110.881 395.765 110.58C384.913 106.901 373.197 107.738 361.922 108.566C350.507 109.413 339.026 108.199 327.667 109.762C323.577 110.194 319.131 111.342 315.191 112.547C313.944 113.093 310.435 114.269 309.254 114.937C308.325 115.417 306.365 116.358 305.427 116.838C304.02 117.581 301.337 119.369 299.958 120.179C298.073 121.656 294.949 123.792 293.233 125.467C292.295 126.418 289.377 128.798 288.543 129.852C287.492 130.944 285.184 133.438 284.134 134.539L283.853 134.294L286.779 131.132C288.364 129.297 291.085 126.738 292.923 125.1C294.649 123.416 297.726 121.223 299.648 119.718C300.999 118.852 303.813 117.064 305.136 116.302C306.552 115.577 308.531 114.58 309.948 113.874C310.482 113.695 314.319 112.142 314.966 111.907C318.962 110.693 323.427 109.507 327.563 109.056C338.941 107.456 350.469 108.632 361.857 107.757C373.216 106.891 385.035 106.016 396.037 109.724C396.928 110.081 398.729 110.58 399.63 110.985C403.701 113.215 406.027 117.487 408.55 121.167C410.079 123.585 412.115 125.909 412.724 128.817C414.732 137.785 405.352 142.669 399.105 146.668C396.244 148.701 393.917 151.496 392.473 154.705C391.282 157.264 390.531 160.04 389.387 162.731C388.29 165.423 386.385 167.879 383.918 169.469C379.106 172.368 374.304 175.878 372.222 181.307C371.152 183.933 370.758 186.756 370.449 189.607C369.717 195.357 368.001 200.909 365.871 206.254C363.292 212.973 359.634 219.466 356.097 225.921"
            fill="#2E7FA1"
          />
          <path
            d="M292.171 131.64C292.171 131.64 292.021 131.301 292.021 131.311C290.389 131.951 286.487 133.513 284.883 134.143C264.829 141.38 243.545 144.937 222.3 145.981C219.401 146.122 216.493 146.207 213.595 146.207C210.303 146.235 207.001 146.151 203.709 145.972C179.94 144.786 156.03 140.551 133.996 131.301L133.837 131.631C140.093 134.397 146.472 136.947 153.066 138.829C172.454 144.466 192.734 147.045 212.91 146.602C239.915 147.092 267.436 142.801 292.181 131.649L292.171 131.64Z"
            fill="#2E7FA1"
          />
          <path
            d="M52.7757 165.413C52.4661 161.075 52.1284 156.671 50.8152 152.521C49.5302 148.362 47.2977 144.588 44.812 141.05C41.0038 135.809 36.567 130.482 35.0193 123.952C34.5316 121.787 34.2314 119.548 34.7192 117.308L35.0756 117.412C34.7192 119.501 35.085 121.721 35.6478 123.792C38.64 134.529 48.7235 141.455 51.5938 152.295C52.8413 156.549 53.0383 161.009 53.1509 165.404C53.1415 165.394 52.7757 165.413 52.7757 165.413Z"
            fill="#2E7FA1"
          />
          <path
            d="M372.887 165.395C372.887 155.608 374.669 148.466 380.456 140.505C385.4 133.748 391.891 126.361 390.962 117.403L391.318 117.299C391.797 119.53 391.497 121.769 391.018 123.943C388.945 132.732 381.873 138.867 377.755 146.556C375.663 150.367 374.388 154.554 373.872 158.883C373.581 161.038 373.403 163.221 373.253 165.414H372.887V165.395Z"
            fill="#2E7FA1"
          />
          {/* End - Pelvis top part */}
          <path
            d="M143.981 291.643C143.981 287.945 136.946 286.769 135.276 282.553C135.201 282.562 135.698 300.931 136.983 301.185C137.461 294.654 143.981 295.586 143.981 291.643Z"
            fill="white"
          />
          <path
            d="M291.718 282.365C291.718 282.365 291.718 282.384 291.718 282.403C290.17 286.75 282.957 287.898 282.957 291.653C282.957 295.615 289.57 294.655 289.945 301.364C289.945 301.374 289.945 301.383 289.945 301.393C289.926 301.477 291.727 282.017 291.718 282.365Z"
            fill="white"
          />
          <path
            d="M159.27 260.053C159.439 260.053 159.617 260.053 159.786 260.062C159.411 259.846 159.082 259.582 158.801 259.281C158.341 259.263 157.891 259.234 157.441 259.206C156.081 259.131 154.721 258.886 153.351 258.999C150.537 259.178 147.976 260.401 145.575 261.606C141.945 263.554 138.127 265.116 134.103 265.69C134.103 265.991 134.094 266.301 134.047 266.603C134.206 266.584 134.356 266.574 134.516 266.546C134.657 280.953 134.131 295.84 138.418 309.787C142.17 321.041 151.034 321.7 159.899 315.395C164.767 312.026 168.95 307.857 173.049 303.689C175.094 301.571 177.421 299.454 178.312 296.527C182.251 285.414 168.819 266.283 159.279 260.044L159.27 260.053ZM135.36 283.616C135.36 283.522 135.36 283.419 135.351 283.315C136.082 284.68 137.358 285.818 138.596 286.665C140.941 288.312 145.885 290.891 142.264 293.864C139.525 295.53 137.424 297.111 136.87 299.877C135.951 294.071 135.641 288.039 135.36 283.616ZM177.477 296.264C176.605 299.078 174.381 301.016 172.402 303.077C168.313 307.227 164.12 311.339 159.364 314.633C144.853 324.533 139.45 314.078 137.18 301.684C137.396 297.911 139.581 296.527 142.799 294.57C144.637 293.403 145.003 290.834 143.474 289.244C141.42 287.004 138.315 286.082 136.373 283.776C135.848 283.174 135.473 282.45 135.276 281.678C135.041 276.615 134.929 271.543 134.882 266.471C138.765 265.793 142.508 264.25 145.95 262.377C148.933 260.853 152.01 259.46 155.359 259.818C156.409 259.902 157.469 259.968 158.529 260.015C160.902 261.361 162.881 263.422 164.757 265.407C171.464 272.936 180.61 285.724 177.477 296.264Z"
            fill="#2E7FA1"
          />
          <path
            d="M292.206 266.546C292.412 266.584 292.628 266.593 292.834 266.621C292.75 266.32 292.694 266.01 292.665 265.699C288.632 265.134 284.795 263.563 281.156 261.615C278.764 260.41 276.194 259.187 273.38 259.008C272.01 258.895 270.65 259.14 269.29 259.215C268.68 259.253 268.071 259.281 267.461 259.309C267.142 259.62 266.757 259.883 266.326 260.081C266.673 260.081 267.02 260.081 267.358 260.062C265.182 261.389 263.381 263.243 261.589 265.05C255.623 271.468 250.314 278.977 248.222 287.635C246.675 294.589 248.532 298.983 253.682 303.689C257.781 307.867 261.964 312.026 266.833 315.395C275.669 321.719 284.608 320.994 288.313 309.786C289.035 307.424 289.617 305.034 290.086 302.635H290.161C290.18 302.475 290.18 302.315 290.189 302.155C291.446 295.586 291.887 288.867 292.056 282.139C292.178 281.668 292.243 281.188 292.215 280.68H292.093C292.187 275.947 292.168 271.223 292.215 266.546H292.206ZM289.926 299.35C289.889 299.219 289.87 299.087 289.823 298.965C289.42 297.76 288.613 296.687 287.647 295.906C286.324 294.72 284.486 294.231 283.651 292.697C282.507 289.714 286.428 288.209 288.351 286.684C289.467 285.922 290.592 284.924 291.343 283.739C291.071 288.02 290.771 293.761 289.917 299.35H289.926ZM291.408 282.468C291.193 282.948 290.921 283.39 290.574 283.795C288.632 286.091 285.527 287.023 283.473 289.263C282.554 290.251 282.178 291.841 282.826 293.102C283.726 294.833 285.771 295.454 287.141 296.556C288.623 297.591 289.392 299.2 289.664 300.922C287.478 313.645 282.15 324.739 267.348 314.633C262.593 311.339 258.4 307.227 254.31 303.077C252.331 301.016 250.108 299.077 249.235 296.264C246.778 287.738 251.843 279.081 256.477 272.202C259.816 267.713 263.399 262.97 268.221 260.025C269.271 259.978 270.322 259.912 271.372 259.827C274.721 259.469 277.798 260.862 280.781 262.387C284.223 264.259 287.957 265.803 291.84 266.48C291.793 271.816 291.68 277.151 291.408 282.468Z"
            fill="#2E7FA1"
          />
          {/* End - Left & Right bottom appendices of the pelvis */}

          {/**
           * Start - Loading baby's body image according to the position and rotation selected by the user
           * The standard values are Hodeleie for position and OA for rotation. The rotation starting by R (right side)
           * implies that the image should be mirrored (from the Left side). The BabyBody component is responsible for
           * mirroring the image when necessary.
           */}
          <g
            transform={`translate(${babyPositionX}, ${
              pxDescentLevel - babyPositionY + 7
            })`}
          >
            <g
              transform={
                babyBodyRotation.startsWith("R")
                  ? `translate(${SVG_CENTER_X},0) scale(-1,1) translate(-${SVG_CENTER_X},0)`
                  : undefined
              }
            >
              <BabyBody
                position={babyBodyPosition}
                rotation={babyBodyRotation}
                synclitism={synclitism}
              />
            </g>
          </g>

          {/* End - Baby's body image */}

          {/* Start - Pelvis bottom part */}
          <g opacity="0.8">
            <path
              d="M352.364 232.323C353.283 230.548 354.482 228.399 355.832 225.948H306.41C304.217 229.966 301.218 233.815 297.141 237.054C279.07 251.408 250.456 240.847 245.029 240.021C239.621 239.223 240.643 242.217 231.092 241.682C221.541 241.138 218.167 244.846 218.167 244.846C217.174 243.034 215.59 241.682 213.04 241.682C210.491 241.682 208.897 243.034 207.913 244.846C207.913 244.846 204.53 241.138 194.979 241.682C185.541 242.208 186.319 239.298 181.07 240.03C181.07 240.03 181.005 240.03 180.967 240.039C180.948 240.039 180.93 240.039 180.911 240.039C175.147 241.025 146.87 251.277 128.977 237.045C124.9 233.806 121.901 229.966 119.708 225.939H70.2578C71.6074 228.38 72.8071 230.53 73.7257 232.295C61.1945 249.268 84.1386 251.267 94.1205 269.545C104.196 287.955 108.573 284.172 112.828 293.494C106.099 301.013 112.959 314.25 116.305 320.747C119.905 327.788 118.874 342.958 129.708 345.671C140.543 348.375 151.903 338.893 165.305 325.356C168.651 321.986 171.501 319.461 174.013 317.461C181.52 311.462 185.925 310.204 191.352 304.506C198.569 296.92 200.631 287.448 213.021 287.448C225.412 287.448 227.455 296.92 234.691 304.506C240.333 310.439 244.87 311.575 252.977 318.24H252.996C255.283 320.099 257.813 322.399 260.747 325.347C274.168 338.884 285.509 348.366 296.344 345.662C307.179 342.94 306.148 327.778 309.766 320.737C313.093 314.241 319.963 301.004 313.224 293.484C317.489 284.172 321.884 287.955 331.951 269.545C337.162 260.017 345.888 254.91 351.277 249.803H351.286C356.226 245.128 358.344 240.434 352.364 232.313V232.323ZM174.969 300.422C169.289 306.411 164.106 311.415 159.373 314.654C154.452 318.005 150.009 319.451 145.97 318.109C141.63 316.663 138.594 312.129 136.719 302.318C136.719 302.318 136.719 302.309 136.719 302.3C136.691 301.689 136.71 301.135 136.756 300.619C136.325 298.084 135.538 295.737 135.613 293.053C135.697 290.133 134.666 281.327 134.666 280.407L134.657 280.398C134.507 276.023 134.432 271.17 134.432 265.828C134.432 265.828 134.441 265.828 134.451 265.828C143.786 264.373 148.06 258.524 155.089 259.209C156.083 259.313 157.039 259.378 157.948 259.425C157.948 259.425 157.939 259.425 157.929 259.425C164.303 262.251 186.572 288.208 174.959 300.431L174.969 300.422ZM291.63 265.818C291.63 271.864 291.526 277.281 291.339 282.097C291.339 282.106 291.339 282.116 291.339 282.135C291.114 282.773 290.514 294.611 289.436 299.878C289.502 300.244 289.558 300.629 289.577 301.051C289.577 301.06 289.577 301.07 289.577 301.079C287.731 311.753 284.619 316.597 280.101 318.099C276.277 319.376 272.088 318.156 267.476 315.17H267.458C262.509 311.969 257.082 306.74 251.084 300.422C239.471 288.19 261.806 262.232 268.189 259.416H268.17C269.079 259.369 270.035 259.303 271.019 259.2C278.058 258.515 282.323 264.363 291.648 265.818C291.648 265.818 291.639 265.818 291.63 265.818Z"
              fill="white"
            />
            <path
              d="M161.744 244.461C167.021 243.823 172.213 242.677 177.33 241.316C179.814 240.706 182.401 239.786 184.913 240.593C186.131 240.978 187.406 241.523 188.746 241.739C191.38 242.255 194.089 242.001 196.722 241.908C200.415 241.926 204.52 242.396 207.492 244.743C206.254 247.043 205.889 250.113 206.161 252.506H206.526C206.939 248.282 207.848 242.293 213.05 241.861C218.251 242.283 219.17 248.282 219.573 252.506H219.938C220.21 250.113 219.845 247.043 218.598 244.733C221.579 242.386 225.674 241.926 229.358 241.908C233.294 242.189 237.493 242.199 241.167 240.603C243.679 239.786 246.266 240.715 248.75 241.326C253.858 242.677 259.06 243.832 264.336 244.47C288.593 247.737 306.71 237.12 311.518 212.317C312.484 207.079 312.774 201.718 312.268 196.414L311.902 196.461C312.305 201.709 311.931 207.041 310.89 212.195C305.82 236.566 288.387 246.658 264.449 243.588C259.219 242.978 254.064 241.823 248.947 240.537C246.379 239.917 243.614 239.007 240.961 239.889C239.658 240.321 238.496 240.847 237.212 241.063C234.625 241.579 232.001 241.363 229.367 241.326C225.571 241.429 221.466 241.964 218.429 244.414C217.314 242.546 215.58 241.279 213.05 241.485C210.528 241.279 208.785 242.546 207.679 244.405C204.633 241.945 200.528 241.401 196.722 241.316C194.079 241.354 191.455 241.57 188.878 241.053C187.593 240.828 186.431 240.312 185.129 239.88C182.467 238.997 179.702 239.899 177.134 240.528C172.007 241.823 166.861 242.968 161.631 243.579C137.694 246.639 120.279 236.575 115.19 212.186C114.15 207.032 113.775 201.7 114.178 196.452L113.812 196.405C113.316 201.709 113.606 207.069 114.562 212.308C119.351 237.092 137.488 247.737 161.744 244.461Z"
              fill="#2E7FA1"
            />
            <path
              d="M169.083 258.12C165.268 259.256 161.135 259.237 157.17 258.993C155.811 258.918 154.452 258.674 153.084 258.787C150.272 258.965 147.713 260.186 145.314 261.387C140.581 263.922 135.52 265.818 130.112 265.696C121.526 266.119 116.371 258.261 111.657 252.225C106.783 245.756 100.813 239.645 93.0898 236.707C89.3595 235.251 84.8044 234.228 81.0741 236.284C77.3719 238.293 75.7879 243.428 77.7187 247.174L78.0373 246.996C76.3034 243.456 77.8311 238.612 81.3459 236.791C84.9356 234.923 89.2001 235.946 92.8086 237.382C100.372 240.387 106.108 246.348 110.945 252.769C115.743 259.021 121.217 267.104 130.102 266.607C131.499 266.635 132.886 266.532 134.264 266.316C134.404 280.689 133.879 295.54 138.163 309.453C141.912 320.681 150.769 321.338 159.626 315.048C164.49 311.687 168.671 307.528 172.766 303.37C174.81 301.257 177.134 299.145 178.024 296.225C181.961 285.138 168.539 266.053 159.007 259.829C162.466 259.941 165.943 259.754 169.289 258.73C170.611 258.374 171.707 257.397 172.879 256.778C174.05 256.158 175.306 255.689 176.581 255.294C179.121 254.553 181.858 254.102 184.445 254.722L184.538 254.365C179.093 253.117 173.535 255.548 169.092 258.12H169.083ZM164.481 265.18C171.182 272.69 180.321 285.448 177.19 295.963C176.319 298.77 174.097 300.703 172.12 302.759C168.033 306.899 163.844 311.002 159.092 314.288C145.033 323.863 139.531 314.353 137.141 302.497C137.047 302.262 136.972 302.027 136.935 301.774C136.532 299.051 135.932 296.62 135.894 293.888C135.501 290.02 135.295 286.321 135.107 283.355C135.107 283.261 135.107 283.158 135.098 283.054C135.173 283.205 135.098 281.693 135.023 281.411C134.789 276.361 134.676 271.301 134.629 266.241C138.509 265.565 142.249 264.025 145.689 262.157C148.669 260.636 151.744 259.247 155.09 259.603C156.139 259.688 157.198 259.754 158.258 259.801C160.629 261.143 162.606 263.199 164.481 265.18Z"
              fill="#2E7FA1"
            />
            <path
              d="M102.874 245.907C99.856 250.526 102.34 256.656 106.586 259.557L106.83 259.275C103.371 255.914 101.093 250.798 103.193 246.094L102.874 245.907Z"
              fill="#2E7FA1"
            />
            <path
              d="M117.29 286.359C121.283 282.688 122.989 277.055 123.336 271.761L122.97 271.723C121.855 276.811 120.346 281.984 117.027 286.096C117.027 286.096 117.29 286.349 117.29 286.359Z"
              fill="#2E7FA1"
            />
            <path
              d="M347.987 239.185C344.931 233.918 337.827 234.651 333.009 236.707C325.277 239.654 319.297 245.766 314.433 252.225C309.718 258.261 304.563 266.109 295.978 265.696C290.57 265.818 285.509 263.922 280.776 261.387C278.386 260.185 275.817 258.965 273.006 258.787C271.637 258.674 270.278 258.918 268.919 258.993C264.955 259.237 260.821 259.256 257.007 258.12C252.564 255.548 247.006 253.116 241.561 254.365L241.654 254.722C244.241 254.102 246.978 254.553 249.518 255.294C250.793 255.679 252.049 256.158 253.22 256.778C254.401 257.397 255.488 258.374 256.81 258.73C260.128 259.744 263.558 259.941 266.988 259.838C264.814 261.162 263.014 263.011 261.224 264.814C255.263 271.216 249.958 278.708 247.868 287.344C246.322 294.282 248.178 298.666 253.323 303.36C257.419 307.528 261.599 311.678 266.464 315.039C275.293 321.347 284.225 320.624 287.927 309.443C288.649 307.087 289.23 304.703 289.698 302.309C289.698 302.309 289.792 301.99 289.792 301.83C291.048 295.277 291.273 288.875 291.638 282.125C291.667 281.646 291.695 280.407 291.695 280.407C291.788 275.685 291.77 270.972 291.817 266.306C293.185 266.522 294.581 266.635 295.978 266.607C304.863 267.104 310.346 259.021 315.136 252.769C319.972 246.338 325.708 240.377 333.281 237.382C336.88 235.946 341.145 234.923 344.744 236.791C348.258 238.612 349.786 243.456 348.052 246.996L348.371 247.174C349.664 244.686 349.355 241.588 347.977 239.185H347.987ZM291.01 282.453C290.973 282.866 290.457 292.236 289.811 297.22C289.736 298.112 289.623 299.004 289.436 299.887C289.389 300.093 289.295 300.553 289.305 300.591C287.121 313.283 281.797 324.351 267.007 314.269C262.255 310.983 258.066 306.881 253.979 302.741C252.002 300.685 249.78 298.751 248.909 295.944C246.453 287.438 251.514 278.802 256.144 271.939C259.481 267.461 263.061 262.73 267.879 259.791C268.929 259.744 269.978 259.679 271.028 259.594C274.374 259.237 277.448 260.627 280.429 262.148C283.869 264.016 287.599 265.555 291.479 266.231C291.432 271.554 291.545 277.159 291.029 282.444L291.01 282.453Z"
              fill="#2E7FA1"
            />
            <path
              d="M319.503 259.557C323.75 256.656 326.234 250.535 323.215 245.907L322.897 246.094C324.996 250.788 322.728 255.914 319.26 259.275L319.503 259.557Z"
              fill="#2E7FA1"
            />
            <path
              d="M302.755 271.752C303.102 277.047 304.808 282.679 308.801 286.35C308.801 286.35 309.063 286.097 309.063 286.087C305.754 281.975 304.236 276.803 303.12 271.714L302.755 271.752Z"
              fill="#2E7FA1"
            />
            <path
              d="M354.923 235.984C354.286 234.67 353.452 233.487 352.608 232.332C353.705 230.201 354.857 228.08 356.029 225.949H355.56L353.817 229.159C353.367 230.098 352.833 231.018 352.327 231.948C352.299 231.901 352.261 231.863 352.233 231.816L351.943 232.041C352.008 232.135 352.064 232.22 352.13 232.314C351.315 233.825 350.584 235.374 350.359 237.073L350.705 237.017C350.715 236.379 350.977 235.693 351.249 235.046C351.624 234.266 352.018 233.497 352.411 232.717C353.18 233.835 353.93 234.961 354.473 236.2C356.957 241.063 355.364 245.588 351.483 249.071C346.713 253.549 341.117 257.201 336.731 262.139C332.194 266.983 329.833 273.348 325.812 278.558C324.893 279.835 323.759 281.159 322.691 282.304C320.479 284.726 317.676 286.557 315.605 289.148C314.621 290.387 313.871 291.786 313.196 293.213C312.681 292.659 312.1 292.124 311.443 291.626L311.228 291.926C317.358 296.188 316.345 304.581 313.974 310.73C312.39 315.687 309.157 320.034 307.854 325.103C305.811 332.726 304.808 343.973 295.304 345.437C279.727 347.887 264.562 326.896 253.418 318.053C248.178 313.49 241.721 310.514 236.603 305.858C228.646 298.657 225.881 286.904 213.069 287.261C211.325 287.308 209.573 287.401 207.885 287.918C199.019 290.321 196.02 300.329 189.534 305.858C187.022 308.205 184.107 310.073 181.183 311.941C175.288 315.631 169.917 320.174 165.015 325.103C156.561 333.167 143.486 347.587 130.833 345.437C127.431 344.996 124.572 342.799 122.895 339.861C119.352 333.862 119.549 326.361 116.437 320.165C112.322 311.922 105.696 299.014 114.909 291.926L114.694 291.626C114.038 292.077 113.447 292.612 112.913 293.184C111.619 290.227 109.501 287.664 106.989 285.646C104.496 283.543 102.209 281.196 100.307 278.558C96.2764 273.348 93.9239 266.983 89.3876 262.139C85.0012 257.191 79.4057 253.54 74.6351 249.071C70.7548 245.597 69.1708 241.054 71.6452 236.2C72.1982 234.961 72.9574 233.816 73.7353 232.68C74.4945 234.079 75.1599 235.515 75.4036 237.055C75.4036 237.055 75.7692 237.073 75.7598 237.092C75.5348 235.374 74.8038 233.825 73.9884 232.323C74.054 232.229 74.1196 232.135 74.1758 232.051L73.8853 231.826C73.8853 231.826 73.8197 231.919 73.7915 231.957C73.2854 231.037 72.7605 230.117 72.3013 229.188C71.7202 228.127 71.1297 227.047 70.5392 225.958H70.0144L71.9076 229.385C72.395 230.361 72.9667 231.328 73.5104 232.314C72.6575 233.487 71.8139 234.679 71.1766 235.994C68.4117 241.073 70.0331 245.907 74.0633 249.662C78.7965 254.244 84.3545 257.82 88.6846 262.777C93.221 267.574 95.5454 273.911 99.6506 279.056C100.616 280.342 101.769 281.675 102.884 282.82C106.352 286.303 110.635 288.791 112.622 293.485C110.916 295.456 109.904 298.019 109.792 300.638C109.445 307.717 112.716 314.345 115.8 320.503C117.402 323.554 117.946 327.028 118.761 330.36C119.952 335.345 121.32 341.044 125.716 344.282C130.177 347.427 136.223 346.967 141.059 344.986C150.769 340.846 158.201 332.97 165.587 325.713C170.433 320.785 175.709 316.278 181.558 312.58C184.463 310.711 187.425 308.806 189.974 306.384C197.894 299.268 200.416 287.43 213.05 287.636C220.951 287.542 225.347 292.189 229.424 298.357C231.42 301.183 233.538 303.999 236.125 306.374C238.675 308.796 241.646 310.693 244.542 312.57C250.39 316.278 255.658 320.775 260.513 325.704C269.267 334.143 282.135 348.507 295.444 346.273C307.554 344.17 306.073 329.168 310.309 320.494C314.237 312.739 319.898 300.929 313.449 293.513C314.171 292.086 314.958 290.697 315.98 289.486C318.689 286.435 322.325 284.238 324.884 280.99C325.296 280.53 326.083 279.553 326.449 279.056C330.554 273.911 332.879 267.574 337.415 262.777C341.745 257.83 347.303 254.244 352.036 249.662C356.066 245.917 357.688 241.063 354.923 235.994V235.984Z"
              fill="#2E7FA1"
            />
          </g>
          {/* End - Pelvis bottom part */}
          <path
            d="M414 -4H14V101H414V-4Z"
            fill="url(#paint0_linear_255_14250)"
          />
          <g transform="translate(0,6.1)">
            {/* Left side of the level lines*/}
            {/* Top Line In Descent Scale Element */}
            <path
              id="TopLineInDescentScaleElement"
              d="M59 208H49"
              stroke="#2E7FA1"
              strokeMiterlimit="10"
            />
            <path d="M59 223.333H49" stroke="#2E7FA1" strokeMiterlimit="10" />
            <path d="M59 238.667H49" stroke="#2E7FA1" strokeMiterlimit="10" />
            <path d="M59 254H49" stroke="#2E7FA1" strokeMiterlimit="10" />
            <path d="M59 269.333H49" stroke="#2E7FA1" strokeMiterlimit="10" />
            <path d="M59 284.667H49" stroke="#2E7FA1" strokeMiterlimit="10" />
            <path d="M59 300H49" stroke="#2E7FA1" strokeMiterlimit="10" />
            <path d="M59 315.333H49" stroke="#2E7FA1" strokeMiterlimit="10" />
            <path d="M59 330.667H49" stroke="#2E7FA1" strokeMiterlimit="10" />
            {/* Bottom Line In Descent Scale Element */}
            <path
              id="BottomLineInDescentScaleElement"
              d="M59 346H49"
              stroke="#2E7FA1"
              strokeMiterlimit="10"
            />

            {/* Right side of the level lines*/}
            <path d="M377 208H367" stroke="#2E7FA1" strokeMiterlimit="10" />
            <path d="M377 223.333H367" stroke="#2E7FA1" strokeMiterlimit="10" />
            <path d="M377 238.667H367" stroke="#2E7FA1" strokeMiterlimit="10" />
            <path d="M377 254L367 254" stroke="#2E7FA1" strokeMiterlimit="10" />
            <path d="M377 269.333H367" stroke="#2E7FA1" strokeMiterlimit="10" />
            <path d="M377 284.667H367" stroke="#2E7FA1" strokeMiterlimit="10" />
            <path d="M377 300H367" stroke="#2E7FA1" strokeMiterlimit="10" />
            <path d="M377 315.333H367" stroke="#2E7FA1" strokeMiterlimit="10" />
            <path d="M377 330.667H367" stroke="#2E7FA1" strokeMiterlimit="10" />
            <path d="M377 346H367" stroke="#2E7FA1" strokeMiterlimit="10" />
          </g>
          {/* Middle dotted line */}
          <path
            d="M367 290.8H59"
            stroke="#2E7FA1"
            strokeMiterlimit="10"
            strokeDasharray="1.87 1.87"
          />
          {/* Bekkeninngang label*/}
          <text
            fill="#2E7FA1"
            fontFamily="Lato"
            fontSize="12"
            fontStyle="normal"
            fontWeight="300"
            pointerEvents="none"
            textAnchor="end"
            x="463"
            y="233"
            style={{ userSelect: "none" }}
          >
            <tspan>{visualizationLabels.PelvicInlet}</tspan>
          </text>
          {/* Spinae label*/}
          <text
            fill="#2E7FA1"
            fontFamily="Lato"
            fontSize="12"
            fontStyle="normal"
            fontWeight="300"
            pointerEvents="none"
            textAnchor="end"
            x="418"
            y="295"
            style={{ userSelect: "none" }}
          >
            <tspan>{visualizationLabels.IschialSpine}</tspan>
          </text>
          {/* Bekkenbunn label*/}
          <text
            fill="#2E7FA1"
            fontFamily="Lato"
            fontSize="12"
            fontStyle="normal"
            fontWeight="300"
            pointerEvents="none"
            textAnchor="end"
            x="449"
            y="356"
            style={{ userSelect: "none" }}
          >
            <tspan>{visualizationLabels.PelvicFloor}</tspan>
          </text>
          {/* Descent level number */}
          <text
            fill="#37A851"
            fontFamily="Lato"
            fontSize="20"
            fontWeight="900"
            pointerEvents="none"
            textAnchor="end"
            x="42"
            y={descentNumberLevel}
            style={{ userSelect: "none" }}
          >
            <tspan>
              {babyDescentLevel > 0 ? "+" + babyDescentLevel : babyDescentLevel}
            </tspan>
          </text>
          {/* Descent level line */}
          <g
            style={{ userSelect: "none" }}
            transform={`translate(0, ${pxDescentLevel + 7})`}
          >
            <path
              d="M58.3937 199.691C54.3639 198.693 50.2703 201.142 49.2373 205.182C48.2043 209.222 50.6401 213.312 54.67 214.31C58.6998 215.307 62.7935 212.858 63.8264 208.818C64.8594 204.778 62.4236 200.688 58.3937 199.691Z"
              fill="#CCEED2"
              stroke="#37A851"
              strokeWidth="3"
              strokeMiterlimit="10"
            />
            <path
              d="M361.936 207.17L64.0635 206.83"
              stroke="#37A851"
              strokeWidth="2.80807"
              strokeMiterlimit="10"
            />
            <path
              d="M371.33 199.691C367.3 198.693 363.207 201.142 362.174 205.182C361.141 209.222 363.577 213.312 367.607 214.31C371.636 215.307 375.73 212.858 376.763 208.818C377.796 204.778 375.36 200.688 371.33 199.691Z"
              fill="#CCEED2"
              stroke="#37A851"
              strokeWidth="3"
              strokeMiterlimit="10"
            />
          </g>
          {/* Start: Clickable/Touchable area */}
          <rect
            visibility={
              isPlayerInProgress ? Visibility.Hidden : Visibility.Visible
            }
            style={{ userSelect: "none" }}
            width="470"
            height="60"
            transform={`translate(0, ${pxDescentLevel + 177})`}
            fill="transparent"
            onMouseDown={() => {
              startClickOrTouch();
            }}
            onTouchStart={() => {
              startClickOrTouch();
            }}
            onMouseMove={(e) => {
              if (arrowDraggable) {
                moveWhileClickOrTouch(e.clientY);
              }
            }}
            onTouchMove={(e) => {
              if (
                arrowDraggable &&
                e.touches !== null &&
                e.touches.length > 0
              ) {
                const touchInstance = e.touches[0];
                moveWhileClickOrTouch(touchInstance.clientY);
              }
            }}
            onMouseUp={(e) => {
              endClickOrTouch(e, e.clientX, e.clientY);
            }}
            onTouchEnd={(e) => {
              if (
                e.nativeEvent !== null &&
                e.nativeEvent.changedTouches !== null
              ) {
                // Retrieves the touch instance from the nativeEvent element (changedTouches).
                // Although the current touch has ended, this object retains information about inactive touches.
                const touchInstance = e.nativeEvent.changedTouches[0];
                endClickOrTouch(
                  e,
                  touchInstance.clientX,
                  touchInstance.clientY
                );
              }
            }}
            onMouseOut={(e) => {
              cancelClickOrTouch(e);
            }}
            onTouchCancel={(e) => {
              cancelClickOrTouch(e);
            }}
          />

          {/* End: Clickable/Touchable area */}
        </g>
        <defs>
          <linearGradient
            id="paint0_linear_255_14250"
            x1="214.007"
            y1="-3.4456"
            x2="214.007"
            y2="99.688"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0.3155" stopColor="white" />
            <stop offset="1" stopColor="white" stopOpacity="0" />
          </linearGradient>
          <clipPath id="clip0_255_14250">
            <rect width="470" height="400" fill="white" />
          </clipPath>
        </defs>
      </svg>
    </div>
  );
}

export default CervixDescent;
